import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SplitText from "./SplitText";
import { useAnchorNav } from "../utils/anchors";

// Three.js and the post-processing stack live in their own chunk so the
// rest of the page can render before they download.
const HeroScene = lazy(() => import("./HeroScene"));

const stack = [
  "React",
  "Next.js",
  "Flutter",
  "React Native",
  "Node.js",
  ".NET",
  "TypeScript",
  "MongoDB",
  "PostgreSQL",
  "Tailwind",
  "Three.js",
  "WordPress",
];

const Section1 = () => {
  const goTo = useAnchorNav();
  const scrollRef = useRef(0);
  const mouseRef = useRef({ x: 0, y: 0 });
  const sectionRef = useRef(null);
  // The 3D scene only renders while the hero is on screen.
  const [sceneActive, setSceneActive] = useState(true);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 768px)").matches &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : true
  );

  useEffect(() => {
    const mql = window.matchMedia("(min-width: 768px)");
    const onChange = (e) => setIsDesktop(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setSceneActive(entry.isIntersecting),
      { threshold: 0.02 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      scrollRef.current = Math.min(y / 800, 1);
    };
    const onMove = (e) => {
      mouseRef.current = {
        x: (e.clientX / window.innerWidth) * 2 - 1,
        y: (e.clientY / window.innerHeight) * 2 - 1,
      };
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  return (
    <section
      id="top"
      ref={sectionRef}
      className="relative w-full min-h-screen overflow-hidden"
    >
      <div className="bg-grid absolute inset-0 pointer-events-none" />

      {isDesktop && (
        <div className="absolute inset-0">
          <Suspense fallback={null}>
            <HeroScene
              scrollRef={scrollRef}
              mouseRef={mouseRef}
              active={sceneActive}
            />
          </Suspense>
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-28 md:pt-36 lg:pt-40 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.7, ease: [0.22, 1, 0.36, 1] }}
          className="flex justify-center"
        >
          <span className="eyebrow">
            <span className="dot" />
            Taking on new clients · 2026
          </span>
        </motion.div>

        <div className="mt-8 text-center">
          <h1 className="font-jamjuree font-semibold tracking-tight text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05]">
            <SplitText
              triggerOnMount
              delay={1900}
              className="block"
              text="Hi, I'm Hamza."
            />
            <motion.span
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 1,
                delay: 2.4,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="block text-gradient mt-2"
            >
              A software consultant who ships.
            </motion.span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 2.95, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-2xl mx-auto text-base md:text-lg text-white/70 leading-relaxed"
          >
            My team and I take web, mobile and desktop products end to end,
            from architecture and design through launch and ongoing support.
            Honest timelines, clean engineering, results that hold up in
            production.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 3.1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-col sm:flex-row gap-4 items-center justify-center"
          >
            <Link to="/contact" className="btn-primary">
              Let's Talk
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12h14M13 5l7 7-7 7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
            <a href="/#portfolio" onClick={goTo("#portfolio")} className="btn-ghost">
              View our work
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.1, delay: 3.4 }}
            className="mt-14 grid grid-cols-3 gap-4 max-w-xl mx-auto"
          >
            {[
              { k: "3+", v: "Years building" },
              { k: "30+", v: "Projects shipped" },
              { k: "30+", v: "Happy clients" },
            ].map((s, i) => (
              <div
                key={i}
                className="glass rounded-2xl px-3 py-4 text-center hover:border-aurora-teal/40 transition-colors"
              >
                <div className="text-2xl md:text-3xl font-semibold text-gradient">
                  {s.k}
                </div>
                <div className="text-[11px] md:text-xs text-white/60 uppercase tracking-wider mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Tech stack marquee */}
      <div className="relative z-10 mt-8 md:mt-12 mb-6 border-y border-white/5 bg-black/20 backdrop-blur-sm py-4 overflow-hidden">
        <div className="marquee gap-12 text-white/40 text-sm uppercase tracking-[0.25em]">
          {[...stack, ...stack].map((s, i) => (
            <span key={i} className="px-6 whitespace-nowrap">
              <span className="text-aurora-teal">✦</span> {s}
            </span>
          ))}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 3.6 }}
        className="hidden md:flex absolute left-1/2 -translate-x-1/2 bottom-8 z-10 flex-col items-center text-white/50 text-[11px] tracking-widest uppercase"
      >
        <span>Scroll</span>
        <div className="w-[1px] h-8 mt-2 bg-gradient-to-b from-aurora-teal to-transparent" />
      </motion.div>
    </section>
  );
};

export default Section1;
